// import external libraries
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

// import hooks
import { requests } from 'RestAPIs/rapini';

// import types
import type { BackLogResponseList } from 'generated/index';

type UseGetBacklogParams = {
  startDate: Date;
  endDate: Date;
  includeAllSummaries: boolean;
};

/**
 * Reads the backlog timeline for every care receiver that has a prescription in the period.
 *
 * `getBacklogTimeline` is a POST — rapini therefore generates it as a mutation — but it is
 * semantically a read, so it is wrapped in `useQuery` here.
 *
 * No `planningEntityIdentifiers` are sent: the backend resolves the care receivers itself, in one
 * query, narrowed to those that actually have a prescription overlapping the period.
 *
 * `includeAllSummaries: false` asks the backend for deviating weeks only — shortages and surpluses
 * alike — and it then also leaves out rules and care receivers that have nothing to report.
 */
export const useGetBacklog = ({
  startDate,
  endDate,
  includeAllSummaries
}: UseGetBacklogParams) => {
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');

  // No onError snackbar: Backlog.tsx already renders an inline error state with a retry button for
  // isError, which is discoverable and actionable where a snackbar just disappears.
  return useQuery<BackLogResponseList>(
    ['backlog', start, end, includeAllSummaries],
    () =>
      requests.getBacklogTimeline({
        period: { start, end },
        includeAllSummaries
      }),
    {
      refetchOnWindowFocus: false
    }
  );
};
